/**
 * Which company's books are open, and the way to change it.
 *
 * Shown at the top of the sidebar (and of the drawer on a phone), so the
 * company is always in view while figures are being read.
 */
import { useState } from 'react'
import { Box, ButtonBase, Menu, MenuItem, Snackbar, Typography } from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../store/AuthContext'
import { errorMessage } from '../api/client'
import { c } from '../theme'

export default function CompanySwitcher() {
  const { me, switchCompany } = useAuth()
  const { t, i18n } = useTranslation()
  const [anchor, setAnchor] = useState<null | HTMLElement>(null)
  /* A failed switch used to be an unhandled promise rejection: the menu
     closed and nothing else happened. Whatever goes wrong, it gets said. */
  const [error, setError] = useState<string | null>(null)
  const [switching, setSwitching] = useState(false)

  const company = me?.companies.find((x) => x.id === me.company_id)
  const multi = (me?.companies.length ?? 0) > 1
  const ar = i18n.language === 'ar'
  const name = (ar ? company?.name_ar : null) || company?.name_en || t('app.name')

  return (
    <>
      <ButtonBase
        onClick={(e) => multi && setAnchor(e.currentTarget)}
        disabled={!multi}
        aria-haspopup={multi ? 'menu' : undefined}
        aria-label={multi ? t('shell.switchCompany', { name }) : undefined}
        sx={{
          width: '100%', minHeight: 48, px: 1.5, py: 1, gap: 1,
          display: 'flex', justifyContent: 'space-between', textAlign: 'start',
          border: `1px solid ${c.border}`, borderRadius: '8px',
          bgcolor: c.surfaceSunken, color: c.ink,
          '&.Mui-disabled': { color: c.ink },
          '&:focus-visible': { outline: '2px solid var(--focus-ring)', outlineOffset: 2 },
        }}>
        <Box sx={{ minWidth: 0 }}>
          {/* Two lines before it truncates: "HiFi General Company — Demo"
              must stay distinguishable from the live company. */}
          <Typography sx={{ fontSize: 13, fontWeight: 600, lineHeight: '18px',
                            display: '-webkit-box', WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
            {name}
          </Typography>
          <Typography sx={{ fontSize: 12, color: c.inkMuted }}>
            {t('shell.finYear', { year: me?.fin_year ?? '' })}
          </Typography>
        </Box>
        {multi && <ExpandMoreIcon fontSize="small" sx={{ color: c.inkMuted }} />}
      </ButtonBase>

      <Menu anchorEl={anchor} open={!!anchor} onClose={() => setAnchor(null)}>
        {me?.companies.map((x) => (
          <MenuItem key={x.id} selected={x.id === me.company_id} disabled={switching}
            onClick={async () => {
              setAnchor(null)
              if (x.id === me.company_id) return
              setSwitching(true)
              setError(null)
              try {
                await switchCompany(x.id)
              } catch (e) {
                setError(errorMessage(e))
              } finally {
                setSwitching(false)
              }
            }}>
            {(ar ? x.name_ar : null) || x.name_en}
          </MenuItem>
        ))}
      </Menu>

      <Snackbar open={!!error} autoHideDuration={8000} onClose={() => setError(null)}
        message={error ?? ''} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} />
    </>
  )
}
